import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Brain, AlertCircle, RefreshCw, Search, Sparkles, Maximize2, Minimize2 } from 'lucide-react';
import { knowledgeService } from '../services/knowledgeService';
import { GraphCanvas } from './GraphCanvas';
import { EntityPanel } from './EntityPanel';
import { GraphFilters } from './GraphFilters';
import { GraphStats } from './GraphStats';

type GraphNode = {
  id: string;
  label: string;
  type: 'Document' | 'Entity' | 'Chunk';
  entityType?: string;
  properties?: Record<string, string>;
};

type GraphEdge = {
  source: string;
  target: string;
  label: string;
  context?: string;
};

type GraphStatsData = {
  totalEntities: number;
  totalRelationships: number;
  returnedNodes: number;
  returnedEdges: number;
  documentCount: number;
  entityTypes: Array<{ type: string; count: number }>;
};

type GraphExplorerProps = {
  orgId?: string;
  userId?: string;
  onSwitchToChat: (query: string) => void;
  docFilter?: string | null;
  onClearDocFilter?: () => void;
};

export const GraphExplorer: React.FC<GraphExplorerProps> = ({ orgId, userId, onSwitchToChat, docFilter, onClearDocFilter }) => {
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [edges, setEdges] = useState<GraphEdge[]>([]);
  const [stats, setStats] = useState<GraphStatsData>({
    totalEntities: 0,
    totalRelationships: 0,
    returnedNodes: 0,
    returnedEdges: 0,
    documentCount: 0,
    entityTypes: [],
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const isFirstRender = useRef(true);
  const lastNodeClickMs = useRef(0);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);
  const [enrichSuggestions, setEnrichSuggestions] = useState<Array<{ from: string; to: string; type: string; context: string; selected: boolean }> | null>(null);
  const [enrichApplied, setEnrichApplied] = useState<number | null>(null);

  // Step 1: Get AI suggestions (dry run)
  const handleEnrich = useCallback(async () => {
    setIsEnriching(true);
    setEnrichSuggestions(null);
    setEnrichApplied(null);
    setError(null);
    try {
      const result = await knowledgeService.enrichGraphPreview(undefined, orgId, userId);
      const suggestions = result.suggestions || [];
      if (suggestions.length === 0) {
        setEnrichApplied(0);
      } else {
        setEnrichSuggestions(suggestions.map(s => ({ ...s, selected: true })));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrichment failed');
    } finally {
      setIsEnriching(false);
    }
  }, [orgId, userId]);

  // Step 2: Apply selected suggestions
  const handleApplyEnrichment = useCallback(async () => {
    if (!enrichSuggestions) return;
    const selected = enrichSuggestions.filter(s => s.selected);
    if (selected.length === 0) {
      setEnrichSuggestions(null);
      return;
    }
    setIsEnriching(true);
    try {
      const result = await knowledgeService.enrichGraphApply(
        selected.map(s => ({ from: s.from, to: s.to, type: s.type })),
        orgId, userId
      );
      setEnrichApplied(result.newRelationships);
      setEnrichSuggestions(null);
      fetchSubgraph();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply enrichment');
    } finally {
      setIsEnriching(false);
    }
  }, [enrichSuggestions, orgId, userId]);

  const fetchSubgraph = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await knowledgeService.getGraphSubgraph({
        search: searchQuery || undefined,
        docName: docFilter || undefined,
        limit: 200,
      }, orgId, userId);

      const freshNodes = data.nodes as GraphNode[];
      setNodes(freshNodes);
      setEdges(data.edges);
      // Keep selectedNode in sync with fresh node data so EntityPanel re-renders
      setSelectedNode(prev => {
        if (!prev) return null;
        const refreshed = freshNodes.find(n => n.id === prev.id);
        return refreshed ? { ...refreshed } : null;
      });
      setStats((prev) => ({
        ...prev,
        totalEntities: data.stats.totalEntities,
        totalRelationships: data.stats.totalRelationships,
        returnedNodes: data.stats.returnedNodes,
        returnedEdges: data.stats.returnedEdges,
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load graph data';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, docFilter, orgId]);

  // Fetch stats on mount
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const statsData = await knowledgeService.getGraphStats(orgId, userId);
        setStats((prev) => ({ ...prev, ...statsData }));
      } catch (err) {
        console.warn('Failed to load graph metadata:', err);
      }
    };
    fetchMeta();
    fetchSubgraph();
  }, [orgId, userId]);

  // Refetch when search or doc filter changes (skip initial mount — handled by mount effect)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const debounce = setTimeout(() => fetchSubgraph(), 400);
    return () => clearTimeout(debounce);
  }, [searchQuery, docFilter]);


  const handleNodeClick = (node: GraphNode) => {
    lastNodeClickMs.current = Date.now();
    setSelectedNode({ ...node });
    // if panel already open, keep it open and show new entity
  };

  const handleNodeHover = (node: GraphNode | null) => {
    setHighlightedNodeId(node?.id || null);
  };

  const handleNavigateToEntity = (entityName: string) => {
    setSearchQuery(entityName);
    setSelectedNode(null);
    const targetNode = nodes.find((n) => n.id === entityName);
    if (targetNode) {
      setSelectedNode(targetNode);
      setHighlightedNodeId(entityName);
    }
  };

  const handleViewInChat = (entityName: string) => {
    onSwitchToChat(entityName);
  };

  // Empty state
  // True empty — no data at all in the graph
  const hasNoData = !isLoading && nodes.length === 0 && !error && !searchQuery;

  if (hasNoData) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <Brain className="w-16 h-16 mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">No Graph Data Yet</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Upload documents with graph processing enabled to build the entity knowledge graph.
        </p>
      </div>
    );
  }

  const containerClass = isFullscreen
    ? 'fixed inset-0 z-50 flex flex-col bg-white'
    : 'flex flex-col border border-gray-200 rounded-lg overflow-hidden';

  return (
    <div className={containerClass}>
      {/* Filters + fullscreen toggle */}
      <div className="flex items-center">
        <div className="flex-1 min-w-0">
          <GraphFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onRefresh={fetchSubgraph}
            onEnrich={handleEnrich}
            onViewDetails={() => setPanelOpen(true)}
            selectedEntityName={selectedNode?.label ?? null}
            isLoading={isLoading}
            isEnriching={isEnriching}
            docFilter={docFilter || undefined}
            onClearDocFilter={onClearDocFilter}
          />
        </div>
        <button
          onClick={() => setIsFullscreen(f => !f)}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center border-l border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Enrichment suggestions (review before applying) */}
      {enrichSuggestions && (
        <div className="border-b border-purple-200 bg-purple-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">
                AI found {enrichSuggestions.length} potential cross-document relationships
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEnrichSuggestions(enrichSuggestions.map(s => ({ ...s, selected: true })))}
                className="text-xs text-purple-600 hover:text-purple-800"
              >
                Select all
              </button>
              <button
                onClick={() => setEnrichSuggestions(enrichSuggestions.map(s => ({ ...s, selected: false })))}
                className="text-xs text-purple-600 hover:text-purple-800"
              >
                Deselect all
              </button>
            </div>
          </div>
          <div className="px-4 pb-2 max-h-48 overflow-y-auto space-y-1">
            {enrichSuggestions.map((s, idx) => (
              <label key={idx} className="flex items-start gap-2 p-2 bg-white rounded border border-purple-100 cursor-pointer hover:bg-purple-50">
                <input
                  type="checkbox"
                  checked={s.selected}
                  onChange={(e) => {
                    const updated = [...enrichSuggestions];
                    updated[idx] = { ...s, selected: e.target.checked };
                    setEnrichSuggestions(updated);
                  }}
                  className="mt-0.5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-purple-800">
                    <span className="font-medium">{s.from}</span>
                    <span className="text-purple-400 mx-1">→</span>
                    <span className="font-medium text-purple-600">{s.type}</span>
                    <span className="text-purple-400 mx-1">→</span>
                    <span className="font-medium">{s.to}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{s.context}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="px-4 py-2 flex items-center justify-end gap-2 border-t border-purple-200">
            <button
              onClick={() => setEnrichSuggestions(null)}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyEnrichment}
              disabled={isEnriching || enrichSuggestions.filter(s => s.selected).length === 0}
              className="px-3 py-1.5 text-xs text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              {isEnriching ? 'Applying...' : `Apply ${enrichSuggestions.filter(s => s.selected).length} relationships`}
            </button>
          </div>
        </div>
      )}

      {/* Enrichment applied result */}
      {enrichApplied !== null && !enrichSuggestions && (
        <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 text-sm border-b border-purple-200">
          <Sparkles className="w-4 h-4" />
          <span>
            {enrichApplied > 0
              ? `Added ${enrichApplied} new cross-document relationships`
              : 'No new relationships found — entities may already be fully connected'}
          </span>
          <button onClick={() => setEnrichApplied(null)} className="ml-auto text-purple-500 hover:text-purple-700 text-xs">
            dismiss
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">Loading graph...</span>
        </div>
      )}

      {/* No results for search */}
      {!isLoading && nodes.length === 0 && searchQuery && !error && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Search className="w-10 h-10 mb-3 text-gray-300" />
          <p className="text-sm text-gray-600 mb-3">
            No entities found for "<span className="font-medium">{searchQuery}</span>"
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Canvas + Panel */}
      {!isLoading && nodes.length > 0 && (
        <div className="flex flex-1 min-h-0" style={isFullscreen ? { height: '100%' } : { height: 'clamp(400px, 60vh, 700px)' }}>
          <GraphCanvas
            nodes={nodes}
            edges={edges}
            selectedNodeId={selectedNode?.id || null}
            highlightedNodeId={highlightedNodeId}
            onNodeClick={handleNodeClick}
            onNodeHover={handleNodeHover}
            onBackgroundClick={() => {
              if (Date.now() - lastNodeClickMs.current < 300) return;
              setSelectedNode(null);
              setPanelOpen(false);
            }}
          />
          <EntityPanel
            node={panelOpen ? selectedNode : null}
            edges={edges}
            onClose={() => { setSelectedNode(null); setPanelOpen(false); }}
            onNavigateToEntity={handleNavigateToEntity}
            onViewInChat={handleViewInChat}
            onGraphRefresh={fetchSubgraph}
            orgId={orgId}
            userId={userId}
          />
        </div>
      )}

      {/* Stats */}
      <GraphStats
        totalEntities={stats.totalEntities}
        totalRelationships={stats.totalRelationships}
        returnedNodes={stats.returnedNodes}
        returnedEdges={stats.returnedEdges}
        entityTypes={stats.entityTypes}
        documentCount={stats.documentCount}
      />
    </div>
  );
};
